import React from 'react';
import useAppForm from '@app/site/_form';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import * as z from 'zod/v4';
import { SubmitButton } from '@app/site/_form/button/SubmitButton';
import { ResetButton } from '@app/site/_form/button/ResetButton';
import {
	useSaveCompanyHotelPriceSettingsMutation,
} from '@app/model/company_hotel/service/CompanyHotelSettingsServiceHooks';
import useToaster from '@app/component/overlays/toaster/ToasterHook';


const RateshopperPriceSettingsPage = React.memo(() => {

	const { companyHotel } = useSelectedCompanyHotel();
	const toaster = useToaster();

	const RangeValidation = z.object({
		from: z
			.number({ message: 'From must be a number' })
			.min(-100, 'From must be between 0 and 100')
			.max(100, 'From must be between 0 and 100'),
		to: z
			.number({ message: 'To must be a number' })
			.min(-100, 'To must be between 0 and 100')
			.max(100, 'To must be between 0 and 100'),
	}).superRefine((range, ctx) => {
		if (range.from >= range.to) {
			ctx.addIssue({
				code: 'custom',
				path: ['to'],
				message: 'To must be greater than From',
			});
		}
	});

	const FormValidation = z.object({
		priceMedianWarningRange: RangeValidation,
		priceRankingPositionThreshold: z
			.number({ message: 'Threshold must be a number' })
			.int('Threshold must be a whole number')
			.min(1, 'Threshold must be at least 1'),
		priceRankingIdealPosition: z
			.number({ message: 'Ideal position must be a number' })
			.int('Ideal position must be a whole number')
			.min(1, 'Ideal position must be at least 1'),
	});

	const savePriceSettingsMutation = useSaveCompanyHotelPriceSettingsMutation({
		onSuccess: () => {
			toaster.success('Settings', 'Price settings saved successfully');
		},
	});

	const form = useAppForm({
		defaultValues: {
			priceMedianWarningRange: companyHotel.settings.calendar.priceMedianWarningRange,
			priceRankingPositionThreshold: companyHotel.settings.calendar.priceRankingPositionThreshold,
			priceRankingIdealPosition: companyHotel.settings.calendar.priceRankingIdealPosition,
		},
		validators: {
			onChange: FormValidation,
		},
		onSubmit: ({ value }) => savePriceSettingsMutation.mutateAsync({
			companyHotelId: companyHotel.id,
			input: value,
		}).catch((e: string) => toaster.error('Settings', e)),

	});

	return <form.AppForm>
		<form onSubmit={(e) => {
			e.preventDefault();
			e.stopPropagation();
		}}>
			<div className="bg-white p-4 sm:p-10 rounded-xl">
				<div><h2 className="text-base font-semibold leading-5 text-gray-900">Price Method</h2>
					<p className="text-sm leading-6 text-gray-500">
						Customize the percentage difference below to trigger alerts as desired.
					</p>

					<fieldset className="mt-3 border-t border-gray-200">
						<div className="relative mb-7 mt-4">
							<dt className="text-sm font-semibold leading-7 text-gray-900">
								How should I set the median setting?
							</dt>
							<dd className="mt-0.5 text-sm text-gray-600">
								Below, you can establish two warning levels for percentage differences between your prices and those
								of your competitors. The first level indicates a warning zone, while the second denotes a danger
								zone.
							</dd>
						</div>

						<h3 className="text-sm font-semibold leading-7 text-gray-900">
							Set warning percentage differences of your price vs. competitors' price</h3>
						<div className="sm:grid grid-cols-5 items-center mb-3 gap-x-2 gap-y-2 space-y-2 sm:space-y-0 sm:max-w-3xl">
							<div className="w-full h-2.5 bg-green-400 mr-1 rounded-full"/>
							<form.AppField name='priceMedianWarningRange.from'>
								{(field)=>{
									return <field.NumberField min={-100} max={100} showControls step={5} />
								}}
							</form.AppField>
							<div className="w-full h-2.5 bg-amber-400 mr-1 rounded-full"/>
							<form.AppField name='priceMedianWarningRange.to'>
								{(field)=>{
									return <field.NumberField min={-100} max={100} showControls step={5} />
								}}
							</form.AppField>
							<div className="w-full h-2.5 bg-rose-400 mr-1 rounded-full"/>
						</div>
					</fieldset>
					<fieldset className="mt-3 border-t border-gray-200">
						<div className="relative mb-7 mt-4">
							<dt className="text-sm font-semibold leading-7 text-gray-900">
								How should I set the ranking settings?
							</dt>
							<dd className="mt-0.5 text-sm text-gray-600">
								Below, you can establish two warning levels for percentage differences between your prices and those of your competitors. The first level indicates a warning zone, while the second denotes a danger zone.
							</dd>
						</div>
						<div className="sm:max-w-sm space-y-4">
							<form.AppField name='priceRankingIdealPosition'>
								{(field)=><field.NumberField label='Ideal Position' min={1} max={companyHotel.competitors!.length + 1} showControls step={1} />}
							</form.AppField>
							<form.AppField name='priceRankingPositionThreshold'>
								{(field)=>{
									return <field.NumberField label='Position Threshold' min={1} max={companyHotel.competitors!.length} showControls step={1} />
								}}
							</form.AppField>

						</div>
					</fieldset>
					<div className="flex items-center justify-end gap-x-4 border-t border-gray-200 pt-4 mt-6">
						<ResetButton>Reset</ResetButton>
						<SubmitButton state="primary">Save</SubmitButton>
					</div>
				</div>
			</div>
		</form>
	</form.AppForm>;
});

export default RateshopperPriceSettingsPage;